import { createColumnHelper } from "@tanstack/react-table"

import Icon from "@/components/Icon"
import {
  BatteryCell,
  DrivingCell,
  LockStateCell,
  StateAgeCell,
} from "@/components/vehicles/VehicleStateCells"
import { FAILURE_REASONS } from "@/constants/vehicles"
import { type VehicleListRow } from "@/hooks/useVehicles"

const columnHelper = createColumnHelper<VehicleListRow>()

// Not a component: a component export here trips react-refresh, which wants this file to export
// only column definitions.
const muted = (text: string, italic = true) => (
  <span className={`text-palette-jungleGreen500 ${italic ? "italic" : ""}`}>{text}</span>
)

export const vehicleColumns = [
  columnHelper.display({
    id: "registration",
    header: "Registration",
    cell: ({ row: { original: row } }) => (
      <div className="flex flex-col gap-1">
        <span className="font-medium text-palette-jungleGreen900">
          {row.vehicle.registration_display}
        </span>
        {/* In INVERS but not Zerity: unlockable, yet invisible to ops elsewhere. */}
        {row.inversReference && !row.zerityReference && (
          <span className="flex items-center gap-1 text-xs text-palette-honeycomb800">
            <Icon name="warning" className="h-3 w-3 text-current" />
            Not in Zerity
          </span>
        )}
        {row.lastFailedCommand && (
          <span className="flex items-center gap-1 text-xs text-indicators-red">
            <Icon name="warning" className="h-3 w-3 text-current" />
            {FAILURE_REASONS[row.lastFailedCommand.failureReason ?? ""] ??
              `Last ${row.lastFailedCommand.eventType} failed`}
          </span>
        )}
      </div>
    ),
  }),
  columnHelper.display({
    id: "depot",
    header: "Depot",
    cell: ({ row: { original: row } }) => {
      if (!row.depotName) return muted("Unknown", false)

      return (
        <div className="flex flex-col">
          <span>{row.depotName}</span>
          {/* A car park belongs to a depot, so it reads as a refinement of it. */}
          {row.carParkName && <span className="text-xs">{row.carParkName}</span>}
        </div>
      )
    },
  }),
  columnHelper.accessor((row) => row.vehicle.vehicle_group, {
    id: "vehicle_group",
    header: "Group",
    cell: (info) => info.getValue() ?? "-",
  }),
  columnHelper.display({
    id: "assigned_to",
    header: "Assigned to",
    cell: ({ row: { original: row } }) => {
      if (!row.assignedTo) return muted("Unassigned")

      return (
        <div className="flex flex-col gap-1">
          <span>{row.assignedTo.personName ?? "Unknown driver"}</span>
          {row.assignedTo.clockedOut && (
            <span className="text-xs text-palette-honeycomb800">Clocked out</span>
          )}
        </div>
      )
    },
  }),
  columnHelper.display({
    id: "battery",
    header: "Battery",
    cell: ({ row: { original: row } }) => (
      <BatteryCell
        batteryPercent={row.vehicle.battery_percent}
        chargingStatus={row.vehicle.charging_status}
      />
    ),
  }),
  columnHelper.accessor((row) => row.vehicle.operational_status, {
    id: "operational_status",
    header: "Status",
    cell: (info) => info.getValue() ?? "-",
  }),
  columnHelper.display({
    id: "lock_state",
    header: "Lock state",
    cell: ({ row: { original: row } }) => <LockStateCell lockState={row.vehicle.lock_state} />,
  }),
  columnHelper.display({
    id: "driving",
    header: "Driving",
    cell: ({ row: { original: row } }) => (
      <DrivingCell
        lockState={row.vehicle.lock_state}
        drivingEnabled={row.vehicle.driving_enabled}
      />
    ),
  }),
  columnHelper.accessor((row) => row.vehicle.lock_state_observed_at, {
    id: "state_age",
    header: "State age",
    cell: (info) => <StateAgeCell observedAt={info.getValue()} />,
  }),
]
